import React, { FC, useMemo } from "react";
import {
	Box,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Checkbox,
	FormControlLabel,
	Typography,
	ListItemText,
} from "@mui/material";
import {
	TreatmentConfigTemplate,
	CmVisitScheduleOption,
	RewardEngineOption,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import { CupResponse, IAnalyteInCup } from "../../domain/cup.interface";

interface TreatmentConfigFieldsProps {
	config: TreatmentConfigTemplate;
	onChange: (updatedConfig: TreatmentConfigTemplate) => void;
	options: {
		behaviors: Event[];
		durations: string[];
		visitsPerPeriod: string[];
		minHoursBetweenVisits: string[];
		cmVisitScheduleOptions: CmVisitScheduleOption[];
		rewardEngineOptions: RewardEngineOption[];
		cups?: CupResponse[];
	};
	showDefaultCheckbox?: boolean;
	showAllowCustomProviderSite?: boolean;
	isAdditionalConfig?: boolean;
}

const TreatmentConfigFields: FC<TreatmentConfigFieldsProps> = ({
	config,
	onChange,
	options,
	showDefaultCheckbox = false,
	showAllowCustomProviderSite = false,
	isAdditionalConfig = false,
}) => {
	// Get available analytes based on selected cups
	const availableAnalytes = useMemo(() => {
		if (!options.cups || !config.selectedCups?.length) {
			return [];
		}

		const selectedCupIds = config.selectedCups;
		const analytesMap = new Map<string, IAnalyteInCup>();

		options.cups
			.filter((cup) => selectedCupIds.includes(cup._id))
			.forEach((cup) => {
				cup.analytes.forEach((analyte) => {
					if (!analytesMap.has(analyte._id)) {
						analytesMap.set(analyte._id, analyte);
					}
				});
			});

		return Array.from(analytesMap.values());
	}, [options.cups, config.selectedCups]);

	const handleFieldChange = (
		field: keyof TreatmentConfigTemplate,
		value:
			| string
			| number
			| boolean
			| undefined
			| Record<string, unknown>
			| string[],
	) => {
		onChange({
			...config,
			[field]: value,
		});
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: isAdditionalConfig ? 2 : 0,
				border: isAdditionalConfig ? "1px solid #e0e0e0" : "none",
				borderRadius: isAdditionalConfig ? 1 : 0,
				backgroundColor: isAdditionalConfig ? "#f9f9f9" : "transparent",
			}}
		>
			{isAdditionalConfig && (
				<Typography variant="subtitle2" fontWeight="bold">
					Additional Activity Configuration
				</Typography>
			)}

			<TextField
				fullWidth
				label="Name"
				value={config.name || ""}
				onChange={(e) => handleFieldChange("name", e.target.value)}
				required
			/>

			<TextField
				fullWidth
				label="Segment"
				type="number"
				value={config.segment || ""}
				onChange={(e) => handleFieldChange("segment", Number(e.target.value))}
				required
			/>

			<FormControl fullWidth required>
				<InputLabel>Duration</InputLabel>
				<Select
					value={config.duration ?? ""}
					onChange={(e) =>
						handleFieldChange("duration", Number(e.target.value))
					}
					label="Duration"
				>
					{options.durations.map((duration) => (
						<MenuItem key={duration} value={duration}>
							{duration}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Period Length"
				type="number"
				value={config.periodLength || ""}
				onChange={(e) =>
					handleFieldChange("periodLength", Number(e.target.value))
				}
				required
			/>

			<TextField
				fullWidth
				label="Range"
				value={config.range || ""}
				onChange={(e) => handleFieldChange("range", e.target.value)}
				required
			/>

			<FormControl fullWidth required>
				<InputLabel>Visits Per Period</InputLabel>
				<Select
					value={config.visitsPerPeriod || ""}
					onChange={(e) =>
						handleFieldChange("visitsPerPeriod", Number(e.target.value))
					}
					label="Visits Per Period"
				>
					{options.visitsPerPeriod.map((visits) => (
						<MenuItem key={visits} value={visits}>
							{visits}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<FormControl fullWidth required>
				<InputLabel>Min Hours Between Visits</InputLabel>
				<Select
					value={config.minHoursBetweenVisits ?? ""}
					onChange={(e) =>
						handleFieldChange("minHoursBetweenVisits", Number(e.target.value))
					}
					label="Min Hours Between Visits"
				>
					{options.minHoursBetweenVisits.map((hours) => (
						<MenuItem key={hours} value={hours}>
							{hours}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Weekly Visits Per Period"
				type="number"
				value={config.weeklyVisitsPerPeriod || ""}
				onChange={(e) =>
					handleFieldChange("weeklyVisitsPerPeriod", Number(e.target.value))
				}
				required
			/>

			<FormControl fullWidth>
				<InputLabel>Reward Engine</InputLabel>
				<Select
					value={config.rewardEngineId || ""}
					onChange={(e) => handleFieldChange("rewardEngineId", e.target.value)}
					label="Reward Engine"
				>
					<MenuItem value="">
						<em>None</em>
					</MenuItem>
					{options.rewardEngineOptions.map((engine) => (
						<MenuItem key={engine.id} value={engine.id}>
							{engine.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<FormControl fullWidth>
				<InputLabel>Visit Schedule Options</InputLabel>
				<Select
					multiple
					value={
						config.cmVisitScheduleOptionIds
							? config.cmVisitScheduleOptionIds.split(",")
							: []
					}
					onChange={(e) => {
						const value = e.target.value as string[];
						handleFieldChange("cmVisitScheduleOptionIds", value.join(","));
					}}
					label="Visit Schedule Options"
					renderValue={(selected) => {
						const selectedArray = selected as string[];
						return selectedArray
							.map((id) => {
								const option = options.cmVisitScheduleOptions.find(
									(opt) => opt.cmVisitScheduleOptions.toString() === id,
								);
								return option?.cmVisitScheduleOptionsDaysDisplay || id;
							})
							.join(", ");
					}}
				>
					{options.cmVisitScheduleOptions.map((option) => (
						<MenuItem
							key={option.cmVisitScheduleOptions}
							value={option.cmVisitScheduleOptions.toString()}
						>
							<Checkbox
								checked={
									config.cmVisitScheduleOptionIds
										?.split(",")
										.includes(option.cmVisitScheduleOptions.toString()) || false
								}
							/>
							{option.cmVisitScheduleOptionsDaysDisplay}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{config.cmVisitScheduleOptionIds && (
				<FormControl fullWidth>
					<InputLabel>Default CM Visit Schedule</InputLabel>
					<Select
						value={
							config.cmVisitSchedule?.cmVisitScheduleOptions?.toString() || ""
						}
						onChange={(e) => {
							const selectedOption = options.cmVisitScheduleOptions.find(
								(opt) =>
									opt.cmVisitScheduleOptions.toString() === e.target.value,
							);
							if (selectedOption) {
								handleFieldChange("cmVisitSchedule", {
									cmVisitScheduleOptions: selectedOption.cmVisitScheduleOptions,
									cmVisitScheduleOptionsDays:
										selectedOption.cmVisitScheduleOptionsDays,
									cmVisitScheduleOptionsDaysDisplay:
										selectedOption.cmVisitScheduleOptionsDaysDisplay,
									cutDays: selectedOption.cutDays || [],
								});
							}
						}}
						label="Default CM Visit Schedule"
					>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{options.cmVisitScheduleOptions
							.filter((opt) =>
								config.cmVisitScheduleOptionIds
									?.split(",")
									.includes(opt.cmVisitScheduleOptions.toString()),
							)
							.map((option) => (
								<MenuItem
									key={option.cmVisitScheduleOptions}
									value={option.cmVisitScheduleOptions.toString()}
								>
									{option.cmVisitScheduleOptionsDaysDisplay}
								</MenuItem>
							))}
					</Select>
				</FormControl>
			)}

			<FormControl fullWidth required>
				<InputLabel>Behavior (Activity)</InputLabel>
				<Select
					value={config.behavior || ""}
					onChange={(e) => handleFieldChange("behavior", e.target.value)}
					label="Behavior (Activity)"
				>
					{options.behaviors.map((behavior) => (
						<MenuItem key={behavior._id} value={behavior._id}>
							{behavior.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<TextField
				fullWidth
				label="Disenrollment Days Threshold"
				type="number"
				value={config.disenrollmentDaysThreshold || ""}
				onChange={(e) =>
					handleFieldChange(
						"disenrollmentDaysThreshold",
						e.target.value ? Number(e.target.value) : undefined,
					)
				}
			/>

			{showDefaultCheckbox && (
				<FormControlLabel
					control={
						<Checkbox
							checked={config.default || false}
							onChange={(e) => handleFieldChange("default", e.target.checked)}
						/>
					}
					label="Set as Default"
				/>
			)}

			{showAllowCustomProviderSite && (
				<FormControlLabel
					control={
						<Checkbox
							checked={config.allowCustomProviderSite || false}
							onChange={(e) =>
								handleFieldChange("allowCustomProviderSite", e.target.checked)
							}
						/>
					}
					label="Allow Custom Provider Site (OU)"
				/>
			)}

			{/* Cups and Analytes Configuration */}
			{options.cups && options.cups.length > 0 && (
				<>
					<FormControlLabel
						control={
							<Checkbox
								checked={config.useCupsAndAnalytes || false}
								onChange={(e) => {
									const isChecked = e.target.checked;
									// Batch all changes together to avoid stale state issues
									onChange({
										...config,
										useCupsAndAnalytes: isChecked,
										// Clear selections when disabling
										...(isChecked
											? {}
											: { selectedCups: [], primaryAnalytes: [] }),
									});
								}}
							/>
						}
						label="Use Cups and Primary Analytes"
					/>

					{config.useCupsAndAnalytes && (
						<>
							<FormControl fullWidth>
								<InputLabel>Selected Cups</InputLabel>
								<Select
									multiple
									value={config.selectedCups || []}
									onChange={(e) => {
										const value = e.target.value as string[];
										handleFieldChange("selectedCups", value);
										// Clear primary analytes that are no longer available
										if (config.primaryAnalytes?.length) {
											const newAvailableAnalyteIds = new Set<string>();
											options.cups
												?.filter((cup) => value.includes(cup._id))
												.forEach((cup) => {
													cup.analytes.forEach((a) =>
														newAvailableAnalyteIds.add(a._id),
													);
												});
											const validAnalytes = config.primaryAnalytes.filter(
												(id) => newAvailableAnalyteIds.has(id),
											);
											if (
												validAnalytes.length !== config.primaryAnalytes.length
											) {
												handleFieldChange("primaryAnalytes", validAnalytes);
											}
										}
									}}
									label="Selected Cups"
									renderValue={(selected) => {
										const selectedArray = selected as string[];
										return selectedArray
											.map((id) => {
												const cup = options.cups?.find((c) => c._id === id);
												return cup?.display || cup?.name || id;
											})
											.join(", ");
									}}
								>
									{options.cups.map((cup) => (
										<MenuItem key={cup._id} value={cup._id}>
											<Checkbox
												checked={
													config.selectedCups?.includes(cup._id) || false
												}
											/>
											<ListItemText primary={cup.display || cup.name} />
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{config.selectedCups && config.selectedCups.length > 0 && (
								<FormControl fullWidth>
									<InputLabel>Primary Analytes</InputLabel>
									<Select
										multiple
										value={config.primaryAnalytes || []}
										onChange={(e) => {
											const value = e.target.value as string[];
											handleFieldChange("primaryAnalytes", value);
										}}
										label="Primary Analytes"
										renderValue={(selected) => {
											const selectedArray = selected as string[];
											return selectedArray
												.map((id) => {
													const analyte = availableAnalytes.find(
														(a) => a._id === id,
													);
													return (
														analyte?.shortnameDisplay ||
														analyte?.shortname ||
														id
													);
												})
												.join(", ");
										}}
									>
										{availableAnalytes.map((analyte) => (
											<MenuItem key={analyte._id} value={analyte._id}>
												<Checkbox
													checked={
														config.primaryAnalytes?.includes(analyte._id) ||
														false
													}
												/>
												<ListItemText
													primary={
														analyte.shortnameDisplay || analyte.shortname
													}
													secondary={analyte.scientificName}
												/>
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						</>
					)}
				</>
			)}
		</Box>
	);
};

export default TreatmentConfigFields;
